import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ClipboardList, Truck, TrendingUp, Boxes, ArrowRight, Monitor, Clock } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/analytics";

export default function AdminHome() {
  const [stats, setStats] = useState({ orders: 0, deliveries: 0, pendingDeliveries: 0, income: 0, inventory: 0 });
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [orders, deliveries, income, inv, act] = await Promise.all([
          api.get("/orders"), api.get("/delivery/requests"), api.get("/income"),
          api.get("/inventory/items"), api.get("/auth/login-activity"),
        ]);
        setStats({
          orders: orders.data.length,
          deliveries: deliveries.data.length,
          pendingDeliveries: deliveries.data.filter((d) => !d.delivered).length,
          income: income.data.reduce((s, i) => s + Number(i.amount || 0), 0),
          inventory: inv.data.length,
        });
        setActivity(act.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const cards = [
    { label: "Orders Logged", value: stats.orders, icon: ClipboardList, to: "/admin/orders" },
    { label: "Pending Deliveries", value: stats.pendingDeliveries, icon: Truck, to: "/admin/deliveries" },
    { label: "Total Income", value: money(stats.income), icon: TrendingUp, to: "/admin/income" },
    { label: "Inventory Items", value: stats.inventory, icon: Boxes, to: "/admin/inventory" },
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard Overview</h1>
      <p className="mt-1 text-sm text-muted-foreground">Quick snapshot of Crystal Pharmacy operations.</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`overview-card-${c.to.split("/").pop()}`}
            className="group rounded-xl border border-border/60 bg-card p-5 transition-shadow hover:shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><c.icon className="h-5 w-5" /></div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <p className="mt-4 font-heading text-2xl font-extrabold">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-border/60 bg-card p-5" data-testid="login-activity">
        <div className="mb-4 flex items-center gap-2">
          <Monitor className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Recent Sign-In Activity</h3>
          <span className="text-xs text-muted-foreground">— review to spot unexpected access</span>
        </div>
        {activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sign-in records yet.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {activity.map((a, i) => (
              <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm" data-testid={`login-event-${i}`}>
                <span className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {a.at ? format(new Date(a.at), "MMM d, yyyy h:mm a") : "—"}
                  {i === 0 && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-primary">Most recent</span>}
                </span>
                <span className="max-w-[60%] truncate text-muted-foreground">{a.user_agent || "Unknown device"}{a.ip ? ` • ${a.ip}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
